/**
 * Onboarding Flow - 3-Step Process
 * Step 1: Welcome & Signup
 * Step 2: BN Number Lookup or Manual Entry
 * Step 3: Profile Review & Complete
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import WelcomeStep from './WelcomeStep';
import BNLookupStep from './BNLookupStep';
import ProfileSetupStep from './ProfileSetupStep';
import apiClient from '../../lib/apiClient';
import { useAuth } from '../../contexts/AuthContext';

export default function OnboardingFlow() {
  const navigate = useNavigate();
  const { updateUser } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [userData, setUserData] = useState(null);
  const [organizationData, setOrganizationData] = useState(null);

  const steps = [
    { number: 1, title: 'Welcome', description: 'Create your account' },
    { number: 2, title: 'Organization', description: 'Find or enter your organization' },
    { number: 3, title: 'Profile', description: 'Review and complete' }
  ];

  const handleWelcomeComplete = (data) => {
    setUserData(data);
    setCurrentStep(2);
  };

  const handleBNLookupComplete = (data) => {
    setOrganizationData(data);
    setCurrentStep(3);
  };

  const handleProfileComplete = async (profile) => {
    const bn = organizationData?.organization?.bn_number;
    const orgName = organizationData?.organization?.name
      || organizationData?.organization?.legal_name || '';

    if (bn) {
      localStorage.setItem('impactplexus_charity_bn', bn);
      localStorage.setItem('impactplexus_org_name', orgName);
      // Persist BN to the user's account in the DB so it survives new device logins
      try {
        await apiClient.patch('/auth/me', { charity_bn: bn, full_name: orgName || undefined });
        updateUser({ charity_bn: bn, ...(orgName ? { full_name: orgName } : {}) });
      } catch (err) {
        // Non-fatal — localStorage fallback still works for this session
        console.warn('Could not persist BN to account:', err?.response?.data?.detail);
      }
    }
    navigate('/dashboard');
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const progress = ((currentStep - 1) / (steps.length - 1)) * 100;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <div className="border-b border-slate-700/50 bg-slate-900/50 backdrop-blur-sm">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-white">
                IMPACT <span className="text-blue-400">PLEXUS</span>
              </h1>
              <p className="text-sm text-slate-400 mt-1">Getting Started</p>
            </div>
            <div className="text-sm text-slate-400">
              Step {currentStep} of {steps.length}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-8">
        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex justify-between mb-4">
            {steps.map((step) => (
              <div
                key={step.number}
                className={`flex flex-col items-center flex-1 ${
                  step.number < steps.length ? 'relative' : ''
                }`}
              >
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-all ${
                    currentStep >= step.number
                      ? 'bg-blue-500 text-white'
                      : 'bg-slate-700 text-slate-400'
                  } ${currentStep === step.number ? 'ring-4 ring-blue-500/30' : ''}`}
                >
                  {step.number}
                </div>
                <div className="text-center mt-2">
                  <div
                    className={`text-sm font-medium ${
                      currentStep >= step.number ? 'text-white' : 'text-slate-400'
                    }`}
                  >
                    {step.title}
                  </div>
                  <div className="text-xs text-slate-500">{step.description}</div>
                </div>
                {step.number < steps.length && (
                  <div
                    className={`absolute top-5 left-1/2 w-full h-0.5 -z-10 ${
                      currentStep > step.number ? 'bg-blue-500' : 'bg-slate-700'
                    }`}
                    style={{ width: 'calc(100% - 2.5rem)' }}
                  />
                )}
              </div>
            ))}
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        {/* Step Content */}
        <Card className="bg-slate-800/50 border-slate-700">
          <div className="p-8">
            {currentStep === 1 && (
              <WelcomeStep onComplete={handleWelcomeComplete} />
            )}
            {currentStep === 2 && (
              <BNLookupStep
                userData={userData}
                onComplete={handleBNLookupComplete}
                onBack={handleBack}
              />
            )}
            {currentStep === 3 && (
              <ProfileSetupStep
                organizationData={organizationData}
                onComplete={handleProfileComplete}
                onBack={handleBack}
              />
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}
